import React from "react";
import { Tag } from "antd";
import ExcelUploadStageModal, {
  ExcelUploadTypeConfig,
} from "../../../../components/upload/shared/ExcelUploadStageModal";
import { UploadReportState } from "../../../../components/upload/shared/uploadReport";
import { normaliseDaySheet, normaliseMaterialSheet, siteDailyApis } from "../../../../api/SiteDaily";
import adminOrgApis from "../../../../api/master/adminOrg";
import { writeValidatedWorkbook } from "./filledWorkbook";
import {
  bucketRows,
  DownloadBlocked,
  FilledSheetDownload,
  inBatches,
  messageColumns,
  moneyColumn,
  numberColumn,
  reportMessageColumns,
  reportStatus,
  rowColumn,
  sheetDate,
  statusColumn,
} from "./dailyUploadShared";

// Material-Stores upload — one row per material issued on a task-day. Each
// task-day in the file is saved exactly as the sheet saves it (a replace-set):
// materials of that task-day left out of the file are removed, and Issued Qty
// 0 removes one. The server replays the sheet's own save.
//
// There is NO blank template here, deliberately: a hand-typed sheet means
// hand-typed task ids, material names and units, which is exactly what the
// filled day sheet exists to remove. One way in — download the day, fill two
// columns, upload it back.

// The FILLED sheet arrives with everything already in it. The clerk types two
// columns — Issued Qty and Actual Rate — and nothing else: identity, category
// and unit come down filled, so the sheet is a worksheet rather than a
// transcription exercise. It is deliberately NARROW: the norm/target figures
// live on the screen, and a reference column nobody reads is a column everyone
// has to scroll past.
//
// Two rules hold this together:
//  * headers, widths and the row arrays below are aligned BY POSITION only —
//    the writer reconciles nothing. Edit the three together.
//  * no column may normalise onto another's header key (the parser strips
//    bracketed units, lowercases and drops non-alphanumerics, and the LEFTMOST
//    column wins). Never "Rate (ref)", "Unit (ref)" or "Qty (ref)" — they would
//    be read as Actual Rate / UOM / Issued Qty. The names below give taskid, activityname,
//    taskdescription, date, category, material, issuedqty, uom, actualrate,
//    remarks, materialunit — all distinct.
// The ABS reference block sits immediately BEFORE the two inputs, so the clerk
// reads the norm and the day's target and then types against it. Every added
// column is reference-only; the clerk still fills just Issued Qty and Actual
// Rate, with UOM on a dropdown.
//
// Header names are chosen so headerKey() (brackets and punctuation dropped,
// lowercased) cannot collide with anything the upload parser reads — it takes
// actualrate/rate, date/issuedate, issuedqty/issuedquantity/qty, material/
// materialcode/productcode, remarks/remark, taskid/task and uom/unit, and the
// LEFTMOST match wins. 'Target Qty' -> targetqty is distinct from qty, and
// 'Target Rate' -> targetrate from rate, because the match is on the whole key.
const FILLED_HEADERS = [
  "Task ID",
  "Activity Name (ref)",
  "Task Description (ref)",
  "Date",
  "Category (ref)",
  "Material",
  "Material Unit (ref)",
  "PUC Factor (ref)",
  "Norm Qty / Unit (ref)",
  "Wastage % (ref)",
  "Target Qty (ref)",
  "Target Rate (ref)",
  "Target Amount (ref)",
  "Target Cost / Unit (ref)",
  "Issued Qty",
  "UOM",
  "Actual Rate",
  "Actual Amount (ref)",
  "Actual Cost / Unit (ref)",
  "Deviation % (ref)",
  "Remarks",
];
const FILLED_WIDTHS = [22, 36, 34, 14, 26, 34, 18, 11, 15, 12, 13, 13, 15, 15, 12, 16, 13, 15, 15, 13, 30];
// 1-based column letters for the three computed columns' formulas.
const COL_TARGET_AMOUNT = "M";
const COL_ISSUED_QTY = "O";
const COL_ACTUAL_RATE = "Q";
const COL_ACTUAL_AMOUNT = "R";
// Money on the amount columns, two decimals on the rest; the percentages are
// written as whole numbers (2, not 0.02) so they need no format at all.
const FILLED_NUM_FMTS: Record<number, string> = {
  8: "#,##0.0000",
  9: "#,##0.000000",
  10: "#,##0.00",
  11: "#,##0.0000",
  12: "#,##0.00",
  13: "#,##0.00",
  14: "#,##0.00",
  18: "#,##0.00",
  19: "#,##0.00",
  20: "+#,##0.0;-#,##0.0",
};
// 1-based column positions: the UOM dropdown, and the two columns the clerk
// actually fills in (tinted on the sheet so it reads itself).
const UOM_COLUMN = FILLED_HEADERS.indexOf("UOM") + 1;
const INPUT_COLUMNS = [FILLED_HEADERS.indexOf("Issued Qty") + 1, FILLED_HEADERS.indexOf("Actual Rate") + 1];

const INSTRUCTIONS: string[][] = [
  ["Material-Stores upload — Instructions"],
  [""],
  ["Task ID", "Required. The task number within this project, e.g. AE-0100/B3.1.1."],
  ["Date", "Required. Write it as 15-Sep-2026 (or 2026-09-15)."],
  ["Material", "Required. The product code (MAXMAT027) or the exact material name — matched only within the task's own material lines."],
  ["Issued Qty", "Required. Write 0 to remove the material from that task-day."],
  ["UOM", "Optional. Blank = the unit already recorded for that task-day, else the material's own unit. Another unit needs an Actual Rate."],
  ["Actual Rate", "Optional. ₹ per unit; the amount is quantity × rate. No rate = no amount."],
  [""],
  ["On a filled sheet", "You type TWO columns: Issued Qty and Actual Rate — they are the amber ones. Task, date, category, material and unit are already filled in; the columns marked (ref) are for reading only."],
  ["Which tasks are listed", "Only tasks that are TRACKED in Task Monitoring and that Civil produced on that date. A project with no tracking configured gets every task with material norms."],
  ["UOM", "On a filled sheet this is a dropdown of the units the upload accepts. Leave it as it is unless the stores issued in another unit — and then the Actual Rate is required, because every fallback price is per the material's own unit."],
  ["Leave a line alone", "A line you do not touch (no Issued Qty, no Actual Rate) is skipped, so a filled sheet uploaded unchanged saves nothing."],
  [""],
  ["Replace rule", "Each task-day in the file replaces what that task-day holds: materials missing from the file are removed (the preview names them). Task-days not in the file are untouched."],
  ["Blank rows", "A row with nothing entered is skipped. On a task-day that has other rows, a material left blank is removed — the preview names it."],
  ["Civil first", "A task-day needs Civil production before materials can be issued against it — upload or record Civil first."],
  ["Locked days", "A day whose daily progress report was submitted cannot be changed."],
  ["Reference columns", "Columns marked (ref) on a filled sheet are for reading only — the upload ignores them."],
];

// The unit names the upload can resolve. Deduped case-insensitively because the
// master repeats names across ids (on this data "Meters" appears five times and
// "Numbers" four) — the resolver keys on the name, so the list must too.
const loadUomNames = async (): Promise<string[]> => {
  try {
    const res = await adminOrgApis.fetchUom();
    if (res?.status !== true || !Array.isArray(res.data)) return [];
    const seen = new Set<string>();
    const names: string[] = [];
    res.data.forEach((item: any) => {
      const name = String(item?.uomName || "").trim();
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return;
      seen.add(key);
      names.push(name);
    });
    return names.sort((a, b) => a.localeCompare(b));
  } catch {
    // No list is better than no sheet: the column stays free text.
    return [];
  }
};

const flattenTasks = (raw: any) =>
  normaliseDaySheet(raw).phases.flatMap((phase) =>
    phase.activities.flatMap((activity) => activity.tasks.map((task) => ({ ...task, activityName: activity.activityName })))
  );

const buildDaySheet = (projectId: number | string) => async (date: string): Promise<number> => {
  // The sheet lists what the site is actually asked about: TRACKED tasks (Task
  // Monitoring) that Civil produced on the date. The server degrades the scope
  // to every task when the project has no tracking configured at all, so a
  // project that never set monitoring up still gets a full sheet.
  const res = await siteDailyApis.materialTasks(projectId, { date, scope: "MONITORED", producedOnly: true });
  if (res?.status !== true) throw new Error(res?.message || "Could not load the day sheet");
  const tasks = flattenTasks(res.data);

  // Civil first — the pre-check. Material issue is consumption against the day's
  // OUTPUT, so a day with no execution captured has nothing to issue against and
  // the upload would reject every row anyway (the server blocks a material row on
  // a task-day with no Civil production). Refuse the download and say which of
  // the two reasons it is, rather than handing back an empty workbook.
  if (!tasks.length) {
    const [trackedRes, producedRes] = await Promise.all([
      siteDailyApis.materialTasks(projectId, { date, scope: "MONITORED" }),
      siteDailyApis.materialTasks(projectId, { date, scope: "ALL", producedOnly: true }),
    ]);
    const tracked = trackedRes?.status === true ? flattenTasks(trackedRes.data).length : 0;
    const producedAnywhere = producedRes?.status === true ? flattenTasks(producedRes.data).length : 0;
    const day = sheetDate(date);
    // Three different problems, three different answers. Telling someone to
    // "record the day's execution" when they already did — it just wasn't on a
    // tracked task — sends them to redo work that exists. Each one blocks the
    // download with a modal: there is no file to hand back, and there is a next
    // step the user has to take.
    if (!tracked) {
      throw new DownloadBlocked(
        `No task is tracked with material norms on ${day}, so there is nothing to issue against.`,
        "No tracked tasks for this date",
        "Set the tasks up in Task Monitoring (Planning & Execution → Task Monitoring), then record the day's Civil execution."
      );
    }
    if (producedAnywhere) {
      throw new DownloadBlocked(
        `Civil execution on ${day} is recorded, but none of it is on a tracked task — ${producedAnywhere} task(s) produced, ${tracked} tracked.`,
        "Nothing tracked produced on this date",
        "Track those tasks in Task Monitoring, or record the execution against the tracked ones. Material issue is only captured against tracked tasks.",
        "civil"
      );
    }
    throw new DownloadBlocked(
      `Civil execution is required first. Nothing was produced on ${day}, and material issue is consumption against that day's output — ${tracked} tracked task(s) are waiting on it.`,
      "Civil execution required",
      "Record the day's execution in the Civil tab (or upload it), then come back for this sheet.",
      "civil"
    );
  }
  const sheets = await inBatches(tasks, 4, (task) =>
    siteDailyApis
      .getMaterials(task.taskId, date)
      .then((r: any) =>
        r?.status === true ? { ...normaliseMaterialSheet(r.data), activityName: task.activityName } : null
      )
      .catch(() => null)
  );
  const num = (value: string) => (value === "" ? "" : Number(value));
  const r4 = (value: number) => Math.round(value * 10000) / 10000;
  const rows: any[][] = [];
  sheets.filter(Boolean).forEach((sheet: any) => {
    // The denominator behind every Cost / Unit on this task's rows. Without a
    // day's output there is nothing to divide by, so those cells stay blank
    // rather than showing a cost per nothing.
    const produced = Number(sheet.producedQty);
    const hasProduced = Number.isFinite(produced) && produced > 0;
    sheet.lines.forEach((line: any) => {
      // Positionally aligned with FILLED_HEADERS. The two inputs are Issued Qty
      // and Actual Rate; both carry what is already stored (blank when nothing
      // is) and the rate is NEVER seeded from the norm or the suggestion — a
      // defaulted rate would be saved as if someone had checked it.
      //
      // The row number is needed up front: the three Actual columns are Excel
      // FORMULAS, not values, so they recompute as the clerk types instead of
      // going stale the moment Issued Qty is edited.
      const excelRow = rows.length + 2; // row 1 is the header
      const targetQty = num(line.targetQtyToday);
      const targetRate = num(line.normRate);
      // '' x anything is still '': an unpriced norm has no target amount, which
      // is a different fact from a target of zero.
      const targetAmount =
        targetQty === "" || targetRate === "" ? "" : r4(Number(targetQty) * Number(targetRate));
      rows.push([
        sheet.taskSequenceId,
        sheet.activityName,
        sheet.taskName,
        sheetDate(date),
        line.categoryName || "Uncategorized",
        // The NAME is what the clerk reads, and it is what gets matched. The
        // product code is still accepted on a hand-made file; it is simply not
        // written here any more.
        line.materialName,
        line.uomName,
        num(line.pucFactor),
        num(line.normQtyPerUnit),
        // Stored as a fraction; the column is headed '%', so write the number
        // the heading promises.
        line.wastage === "" ? "" : r4(Number(line.wastage) * 100),
        targetQty,
        targetRate,
        targetAmount,
        targetAmount === "" || !hasProduced ? "" : r4(Number(targetAmount) / produced),
        num(line.qtyIssuedToday),
        line.uomName,
        num(line.rate),
        {
          formula: `IF(OR(${COL_ISSUED_QTY}${excelRow}="",${COL_ACTUAL_RATE}${excelRow}=""),"",${COL_ISSUED_QTY}${excelRow}*${COL_ACTUAL_RATE}${excelRow})`,
        },
        hasProduced
          ? {
              formula: `IF(${COL_ACTUAL_AMOUNT}${excelRow}="","",${COL_ACTUAL_AMOUNT}${excelRow}/${produced})`,
            }
          : "",
        {
          formula: `IF(OR(${COL_ACTUAL_AMOUNT}${excelRow}="",${COL_TARGET_AMOUNT}${excelRow}="",${COL_TARGET_AMOUNT}${excelRow}=0),"",(${COL_ACTUAL_AMOUNT}${excelRow}-${COL_TARGET_AMOUNT}${excelRow})/${COL_TARGET_AMOUNT}${excelRow}*100)`,
        },
        line.remarks,
      ]);
    });
  });
  // The unit master the material lines actually live in (uom_master) — the same
  // list the screen's UOM picker offers. A dropdown is the only way the sheet
  // can keep a typo out of a column the upload resolves by name.
  const uomNames = await loadUomNames();
  await writeValidatedWorkbook(
    `material-stores-${date}.xlsx`,
    {
      name: "Issues",
      headers: FILLED_HEADERS,
      widths: FILLED_WIDTHS,
      rows,
      inputColumns: INPUT_COLUMNS,
      numFmts: FILLED_NUM_FMTS,
      dropdowns: uomNames.length
        ? [
            {
              column: UOM_COLUMN,
              values: uomNames,
              errorTitle: "Unknown unit",
              error: "Pick a unit from the list — the upload resolves it against the unit master.",
            },
          ]
        : undefined,
    },
    INSTRUCTIONS
  );
  return rows.length;
};

const previewColumns: any[] = [
  statusColumn,
  rowColumn,
  {
    title: "Task",
    key: "task",
    width: 240,
    ellipsis: true,
    render: (_: any, row: any) => (
      <span>
        <b>{row.TaskID}</b> {row.taskName}
      </span>
    ),
  },
  { title: "Date", dataIndex: "Date", key: "Date", width: 104 },
  {
    title: "Material",
    key: "material",
    width: 240,
    ellipsis: true,
    render: (_: any, row: any) => row.materialName || row.Material,
  },
  numberColumn("Issued Qty", "IssuedQty", 100),
  { title: "UOM", dataIndex: "UOM", key: "UOM", width: 90 },
  moneyColumn("Actual Rate", "ActualRate", 110),
  moneyColumn("Amount", "Amount", 120),
  {
    title: "₹ / unit",
    key: "perUnit",
    width: 130,
    align: "right" as const,
    render: (_: any, row: any) =>
      row.PerUnit === "" || row.PerUnit == null ? (
        <span style={{ color: "#8094ae" }}>—</span>
      ) : (
        <span className="tabular-nums">
          ₹{Number(row.PerUnit).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          <span style={{ color: "#8094ae" }}> / {/cubi[ct]\s*met/i.test(row.workUomName || "") ? "Cum" : row.workUomName || "unit"}</span>
        </span>
      ),
  },
  numberColumn("Produced", "ProducedQty", 100),
  { title: "Remarks", dataIndex: "Remarks", key: "Remarks", width: 180, ellipsis: true },
  ...messageColumns,
];

const taskDays = (report: UploadReportState) =>
  new Set(report.rows.map((row: any) => `${row.TaskID}|${row.Date}`)).size;

const MaterialIssueUploadModal = ({
  open,
  onClose,
  onSuccess,
  projectId,
  initialDate,
  onRecordCivil,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  projectId: number | string;
  initialDate?: string;
  // Where "Record Civil execution" goes when the day has no output to issue
  // against. The page owns the routing; this dialog only offers the button.
  onRecordCivil?: (date: string) => void;
}) => {
  const config: ExcelUploadTypeConfig = React.useMemo(
    () => ({
      title: "Material-Stores — Excel upload",
      entityLabel: "Material issues",
      introSection: (
        <FilledSheetDownload
          label="Or start from a day's sheet"
          initialDate={initialDate}
          hint="Every TRACKED task Civil produced that day, with its material lines, category and units already filled in. Type Issued Qty and Actual Rate only, then upload it back. Civil execution has to be recorded for the day first."
          build={buildDaySheet(projectId)}
          actions={
            onRecordCivil
              ? { civil: { label: "Record Civil execution", run: onRecordCivil } }
              : undefined
          }
        />
      ),
      startUpload: (formData) => siteDailyApis.uploadMaterialsExcel(formData),
      buildBody: (comments) => ({ projectId: String(projectId), comment: comments ? comments : "N/A" }),
      previewColumns,
      previewScrollX: 2000,
      previewPageSize: 100,
      bucketRows,
      summaryTags: (report: UploadReportState) =>
        report.rows.length ? <Tag color="processing">{taskDays(report)} task-day(s)</Tag> : null,
      fullScreen: true,
      confirmClose: true,
      reportExport: {
        fileName: "material-stores-upload-report.xlsx",
        columns: [
          { header: "Status", width: 12, value: reportStatus },
          { header: "Row", width: 8, value: (row: any) => row.Row ?? "" },
          { header: "Task ID", width: 22, value: (row: any) => row.TaskID || "" },
          { header: "Date", width: 12, value: (row: any) => row.Date || "" },
          { header: "Material", width: 32, value: (row: any) => row.materialName || row.Material || "" },
          { header: "Issued Qty", width: 12, value: (row: any) => row.IssuedQty ?? "" },
          { header: "UOM", width: 10, value: (row: any) => row.UOM || "" },
          { header: "Actual Rate", width: 12, value: (row: any) => row.ActualRate ?? "" },
          { header: "Amount", width: 14, value: (row: any) => row.Amount ?? "" },
          { header: "₹ / unit", width: 12, value: (row: any) => row.PerUnit ?? "" },
          ...reportMessageColumns,
        ],
      },
      rowErrorLabel: (row: any) => `Row ${row.Row ?? ""}`,
      copy: {
        uploadIntro: (
          <p>
            Start from the day's sheet below — it lists every tracked task Civil produced that day with its
            materials, category and units already filled in, and you type only <b>Issued Qty</b> and{" "}
            <b>Actual Rate</b>. Each task-day in the file <b>replaces what that task-day holds</b> — materials you
            leave out are removed, and the preview names them before anything is saved.
          </p>
        ),
        validatingHeading: "Checking your file…",
        validatingSub: "Matching tasks and materials, pricing each line and running the same checks the sheet's Save runs.",
        committingHeading: "Saving material issues…",
        committingSub: "Saving every task-day in one go — if anything fails, nothing is saved.",
        commitCta: "Save material issues",
        doneHeading: "Material issues saved",
        doneSub: (report: UploadReportState) => <>{taskDays(report)} task-day(s) saved from {report.rows.length} row(s).</>,
        creationFailedNote: "Nothing was saved — see the reason below.",
      },
      discardOnCancelFromPreview: false,
    }),
    [projectId, initialDate, onRecordCivil]
  );

  return (
    <ExcelUploadStageModal config={config} open={open} onClose={onClose} onSuccess={onSuccess} projectId={projectId} />
  );
};

export default MaterialIssueUploadModal;
